import React, { useCallback, useContext, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';
import UdeDataView from 'app/views/ude/UdeDataView';
import QueryModel, { QuerySettings } from 'app/stores/ude/QueryModel';
import { Button, EmptyState } from 'core/components';
import { SynStore } from 'app/stores/synthetics/synthTypes';
import { transformQueryFilters } from 'app/stores/ude/transformFilters';
import { Filters, Query } from '@kentik/ui-shared/query/query.types';
import BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import $auth from 'app/stores/$auth';
import $ude from 'app/stores/ude/$ude';
import { addFilterGroup, mapFilters } from '@kentik/ui-shared/util/filters';
import { Variable } from '@kentik/ui-shared/boards/boards';
import { VariableMapping } from '@kentik/ui-shared/boards/panels';
import { convertBoardVariablesToFilters } from 'app/stores/boards/convertBoardVariablesToFilters';
import { useGuidedValueResolver } from 'app/views/boards/guidedDimensionLookups';
import { ChartSyncContext } from 'app/views/boards/useChartSync';

import { UdeFormProps } from './form.types';
import UdeEmptyState from './ai/UdeEmptyState';

const VARIABLE_REF_PATTERN = /\{\{(.+?)}}/g;

/**
 * Replaces {{variableName}} references in existing filter values with the current variable value.
 * Supports partial substitution, e.g. `kt_{{foo}}` becomes `kt_<value>`.
 * Filters referencing an unresolved variable are removed entirely.
 */
function substituteVariableReferences(query: Query | undefined, variables: Variable[]): Query | undefined {
  if (!query || !query.filters) {
    return query;
  }

  const variableMap = new Map(
    variables.map((v) => [v.name, Array.isArray(v.value) ? (v.value[0] ?? '') : (v.value ?? '')])
  );

  const substituteGroup = (group: Filters) =>
    mapFilters(group as Parameters<typeof mapFilters>[0], (filter) => {
      if (typeof filter.filterValue !== 'string' || !filter.filterValue.includes('{{')) {
        return filter; // Fast path: no template references
      }
      let hasUnresolved = false;
      const newValue = filter.filterValue.replace(VARIABLE_REF_PATTERN, (_, varName: string) => {
        const resolved = variableMap.get(varName);
        if (!resolved) {
          hasUnresolved = true;
        }
        return resolved ?? '';
      });
      return hasUnresolved ? null : { ...filter, filterValue: newValue };
    }) as Filters;

  return {
    ...query,
    filters: {
      ...query.filters,
      ...(query.filters?.dimensions ? { dimensions: substituteGroup(query.filters.dimensions) } : {}),
      ...(query.filters?.metrics
        ? { metrics: substituteGroup(query.filters.metrics as Filters) as typeof query.filters.metrics }
        : {})
    }
  };
}

interface Config {
  data: Query;
  chart: QuerySettings['chart'];
  variableMappings?: VariableMapping[];
}

interface Props {
  panel: BoardPanelModel;
  config: Config;
  form?: UdeFormProps['form'];
  onEditPanel: (panel: BoardPanelModel) => void;
  canCustomize: boolean;
  $syn?: SynStore;
}

function getQueryModelProps(config: Config, panel: BoardPanelModel): { query: Query; settings: QuerySettings } {
  return {
    query: config.data,
    settings: {
      chart: {
        ...config.chart,
        legend: {
          enable: config.chart?.legend?.enable ?? true,
          position: config.chart?.legend?.position ?? 'right',
          mode: config.chart?.legend?.mode ?? 'condensed',
          showRollups: config.chart?.legend?.showRollups ?? true,
          showEventsView: config.chart?.legend?.showEventsView ?? true
        },
        xAxis: {
          timezone: panel.board.get('config').timezone || config.chart?.xAxis?.timezone || $auth.userTimezone
        },
        yAxis: {
          type: config.chart?.yAxis?.type ?? 'linear',
          metric: config.chart?.yAxis?.metric || ''
        },
        thresholds: config.chart?.thresholds ?? undefined
      }
    }
  };
}

function UdePanelBase({ panel, config, form, onEditPanel, canCustomize, $syn }: Props) {
  const { query, settings } = getQueryModelProps(config, panel);
  const registerChart = useContext(ChartSyncContext);
  // When the query changes, we want to re-run the measurement query with the new measurement name prefix filter (if applicable).
  // On fresh UDE panel, query will be undefined and we want to fetch all measurements, so we pass undefined for measurementName in that case.
  const { measurements } = useDictionaryMeasurements({ measurementName: query?.measurement });

  // Consume a pending query stashed by "Open in UDE" navigation.
  // Only consume once form is available — consumePendingQuery() clears the stored value,
  // so calling it before form is ready would silently drop the handoff.
  useEffect(() => {
    if (!form) {
      return;
    }
    const pending = $ude.consumePendingQuery();
    if (pending) {
      const { query: pendingQuery, chart } = pending;

      // Bulk-set form fields from the pending query. setValuesDeep uses lodash get
      // to extract values by dot-path and falls back to current values for absent fields.
      form.setValuesDeep({
        config: {
          data: pendingQuery,
          chart: chart ?? {}
        }
      });

      // Push form state to panel model to trigger query execution
      const formConfig = form.getValues()?.config;
      if (formConfig) {
        panel.set({ config: formConfig });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // TODO: let's find a way to get rid of this indiscriminate collection fetching
  // Pre-fetch agents and tests so they're available for filters
  useEffect(() => {
    if ($syn?.agents && !$syn.agents.hasFetched) {
      $syn.agents.fetch();
    }
    if ($syn?.tests && !$syn.tests.hasFetched) {
      $syn.tests.fetch();
    }
  }, [$syn]);

  // Read board config outside useMemo so MobX observer tracks it.
  // When board.set('config', ...) is called, this re-renders and recomputes queryWithVariables.
  const boardConfig = panel.board.get('config');
  const boardVariables = React.useMemo(
    () => (boardConfig?.variables || []).filter((variable) => variable.enabled !== false),
    [boardConfig?.variables]
  );

  // Board-level time/autorefresh overrides — read outside useMemo so MobX observer tracks them.
  const boardTimeRange = boardConfig?.time;
  const boardUpdateFrequency = boardConfig?.updateFrequency;

  // Panel-level mappings from config.variableMappings — stabilized with useMemo so the
  // reference only changes when the actual mapping data changes, not on every render.
  const variableMappings = React.useMemo(
    () =>
      (config.variableMappings ?? []).reduce<Record<string, VariableMapping>>(
        (acc, variableMapping: VariableMapping) => {
          if (!variableMapping.disabled) {
            acc[variableMapping.variableName] = variableMapping;
          }
          return acc;
        },
        {}
      ),
    [config.variableMappings]
  );

  const resolveGuidedValue = useGuidedValueResolver();

  // Apply board variables to query before creating QueryModel
  const queryWithVariables = React.useMemo<Query>((): Query => {
    // Substitute any {{variableName}} references in existing filters first.
    const queryAfterSubstitution = substituteVariableReferences(query, boardVariables);
    if (!queryAfterSubstitution) {
      return query as Query;
    }

    let queryAfterVariables: Query;

    if (boardVariables.length === 0 || Object.keys(variableMappings).length === 0) {
      queryAfterVariables = queryAfterSubstitution as Query;
    } else {
      // Add filters to query that are not being substituted, based on variable mappings. This allows variables to add filters to the query even if they're not directly referenced in existing filter values.
      const variableFilterGroup = convertBoardVariablesToFilters(boardVariables, variableMappings, resolveGuidedValue);

      if ((variableFilterGroup.filters?.length ?? 0) === 0 && (variableFilterGroup.filterGroups?.length ?? 0) === 0) {
        queryAfterVariables = queryAfterSubstitution as Query;
      } else {
        const existingDimensions =
          queryAfterSubstitution.filters?.dimensions ?? ({ connector: 'All' as const, filterGroups: [] } as Filters);

        const updatedDimensions = addFilterGroup(existingDimensions, variableFilterGroup, 'All') as Filters;

        queryAfterVariables = {
          ...queryAfterSubstitution,
          filters: {
            ...(queryAfterSubstitution.filters ?? {}),
            dimensions: updatedDimensions
          }
        };
      }
    }

    // Apply board-level time range override when the user has changed the toolbar time.
    // This mirrors how board variables overlay filters on top of the panel's stored config,
    // without permanently modifying the saved panel query.
    let result = queryAfterVariables;
    if (boardTimeRange !== undefined) {
      result = { ...result, time: boardTimeRange };
    }

    // Apply board-level auto-refresh override when set.
    if (boardUpdateFrequency !== undefined) {
      result = { ...result, updateFrequency: boardUpdateFrequency };
    }

    return result;
  }, [query, boardVariables, variableMappings, boardTimeRange, boardUpdateFrequency, resolveGuidedValue]);

  // Create a query transformer for metric filter unit conversion before sending queries to the server.
  const queryTransformer = useCallback((q: Query) => transformQueryFilters(q, measurements), [measurements]);

  const [queryModel] = useState(
    () => panel.queryModel || new QueryModel(queryWithVariables, settings, undefined, queryTransformer)
  );

  // Keep queryTransformer in sync when measurements change
  useEffect(() => {
    queryModel.queryTransformer = queryTransformer;
  }, [queryModel, queryTransformer]);

  useEffect(() => {
    panel.queryModel = queryModel;
  }, [panel, queryModel]);

  useEffect(() => {
    if (!isEqual(queryModel.query, queryWithVariables)) {
      queryModel.query = queryWithVariables;
      queryModel.dirty = true;
      if (queryModel.hasRun) {
        queryModel.run();
      }
    }
    if (!isEqual(queryModel.settings, settings)) {
      queryModel.settings = settings;
      queryModel.dirty = true;
    }
  }, [queryModel, queryWithVariables, settings]);

  // Trigger query execution by pushing form state to panel model
  const handleRunQuery = useCallback(() => {
    if (form) {
      const formConfig = form.getValues()?.config;
      if (formConfig) {
        panel.set({ config: formConfig as BoardPanelModel['attributes']['config'] });
      }
    }
  }, [form, panel]);

  // Callback to update categoryPrefix state in the sidebar isn't needed here;
  // the empty state just needs to populate the form and run the query.
  const handleQueryApplied = useCallback(() => {
    // The empty-state AI input only needs to populate the form and trigger a query run.
  }, []);

  const { board } = panel;

  const handleZoomToSelection = useCallback(
    (start: number, end: number) => {
      if (form) {
        form.setValue('config.data.time.lookback', 0);
        form.setValue('config.data.time.start', start);
        form.setValue('config.data.time.end', end);
        const formConfig = form.getValues()?.config;
        if (formConfig) {
          panel.set({ config: formConfig as BoardPanelModel['attributes']['config'] });
        }
      } else {
        board.setTimeRange({ lookback: 0, start, end });
      }
    },
    [board, form, panel]
  );

  if (!queryModel.query?.metrics?.length) {
    if (!form) {
      if (canCustomize) {
        return (
          <EmptyState
            action={
              <Button icon="edit" text="Edit Panel" intent="primary" outlined onClick={() => onEditPanel(panel)} />
            }
          />
        );
      }
      return null;
    }
    return (
      <UdeEmptyState
        form={form}
        measurements={measurements}
        onQueryApplied={handleQueryApplied}
        onRunQuery={handleRunQuery}
      />
    );
  }

  return (
    <UdeDataView
      queryModel={queryModel}
      onChartRef={registerChart?.(String(panel.id))}
      onZoomToSelection={handleZoomToSelection}
    />
  );
}

const UdePanel = inject('$syn')(observer(UdePanelBase));

export default UdePanel;
